import { useState, useEffect, useMemo, useRef, useId } from 'react'
import { useQuery } from '@tanstack/react-query'
import VaulDrawer from '../BottomSheetComponents/VaulDrawer'
import { Reorder, useDragControls } from 'framer-motion'
import { lightImpactVibrate, trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { fetchBrowseCategories, PeerAnalyserDisplayFilter } from '../../repository/peer-analyser'
import { buildBrowseCategoriesData } from '../../utils/buildBrowseCategoriesData'
import { getFundNatureFromTabId, getTabIdFromFundNature } from '../../utils/peerAnalyserUtils'
import { BrowseTabData, FundNature, BrowseTabId } from '../../types/peer-analyser'
import { usePeerAnalyserStore } from '../../state-management/peerAnalyserStore'
import { useToast } from '../../state-management/contexts/ToastContext'
import RenderHTML from '../ui-components/RenderHTML'
import { getCloseIcon } from '../elite/ElitePageBackBar'
import { useReorderColumns } from '../../hooks/useReorderColumns'
import { getBottomPadding } from '../../utils/sessionStorage'
import { SELECT_CATEGORY_HEADING_ID } from './selectCategoryConstants'

export interface ColumnItem {
  id: string
  label: string
}

// #757575 on white ≥3:1 non-text contrast (PowerSelect_B02 iOS / radio outline)
const UnCheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <circle cx="10" cy="10" r="9" stroke="#757575" stroke-width="2"/>
</svg>`

const CheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9968 7.34881 18.9422 4.80712 17.0676 2.93244C15.1929 1.05776 12.6512 0.00317572 10 0V0ZM14.884 8.384L9.384 13.884C9.26793 14.0001 9.13011 14.0922 8.97844 14.1551C8.82676 14.2179 8.66418 14.2502 8.5 14.2502C8.33582 14.2502 8.17325 14.2179 8.02157 14.1551C7.8699 14.0922 7.73208 14.0001 7.616 13.884L5.116 11.384C4.88155 11.1495 4.74984 10.8316 4.74984 10.5C4.74984 10.1684 4.88155 9.85045 5.116 9.616C5.35046 9.38155 5.66844 9.24984 6 9.24984C6.33157 9.24984 6.64955 9.38155 6.884 9.616L8.5 11.232L13.116 6.616C13.2321 6.49991 13.3699 6.40782 13.5216 6.345C13.6733 6.28217 13.8358 6.24984 14 6.24984C14.1642 6.24984 14.3267 6.28217 14.4784 6.345C14.6301 6.40782 14.7679 6.49991 14.884 6.616C15.0001 6.73209 15.0922 6.86991 15.155 7.02158C15.2178 7.17326 15.2502 7.33583 15.2502 7.5C15.2502 7.66417 15.2178 7.82674 15.155 7.97842C15.0922 8.13009 15.0001 8.26791 14.884 8.384Z" fill="#232323"/>
</svg>`

// Component for each reorderable item with its own drag controls
function ReorderableColumnItem({
  columnId,
  column,
  isSelected,
  isLast,
  onToggle,
  isLocked = false
}: {
  columnId: string
  column: ColumnItem
  isSelected: boolean
  isLast: boolean
  onToggle: (columnId: string) => void
  isLocked?: boolean
}) {
  const dragControls = useDragControls()
  const checkboxId = `display-metric-${columnId}`

  return (
    <Reorder.Item
      value={columnId}
      dragControls={dragControls}
      dragListener={false}
      // Explicit listitem: WebKit/TalkBack drop list semantics when list-style:none (B21 / Android B17)
      role='listitem'
      whileDrag={{
        scale: 1.05,
        zIndex: 50,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
      }}
      className={`relative transition-colors bg-[#F6F9F9] list-none ${!isLast ? 'border-b-[0.5px] border-grey60' : ''}`}
    >
      <div className='flex items-center gap-3 pr-4 py-5 pl-5'>
        <label
          htmlFor={checkboxId}
          className={`flex flex-1 items-center gap-3 min-w-0 ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span className={`flex-1 font-matter text-sm ${isLocked ? 'text-[#6A6E6E]' : 'text-[#232323]'}`}>
            {column.label}
          </span>
          <span
            className={`flex-shrink-0 relative ${isLocked ? 'opacity-60' : ''}`}
            onMouseDown={(e) => {
              e.stopPropagation()
            }}
            onPointerDown={(e) => {
              e.stopPropagation()
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
            }}
          >
            {/* Native checkbox for role/state/operability (Power Select_B14–B16) */}
            <input
              id={checkboxId}
              type='checkbox'
              className='absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed'
              checked={isSelected}
              disabled={isLocked}
              // Power select_B20: checkbox hint (Excel: “double tap to toggle”)
              aria-description={isLocked ? undefined : 'double tap to toggle'}
              onChange={() => {
                if (!isLocked) onToggle(columnId)
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <span aria-hidden='true' className='pointer-events-none block'>
              <RenderHTML innerHTMLText={isSelected ? CheckedIcon : UnCheckedIcon} />
            </span>
          </span>
        </label>
        <div
          id={`drag-handle-${columnId}`}
          className='flex-shrink-0 cursor-grab active:cursor-grabbing'
          style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            dragControls.start(e)
          }}
          onTouchStart={(e) => {
            // Safari needs explicit touch handling - prevent scroll and start drag
            e.preventDefault()
            e.stopPropagation()
            // Use the touch event directly - framer-motion should handle it
            if (e.touches.length > 0) {
              const touch = e.touches[0]
              // Create pointer event-like object for dragControls
              const pointerLikeEvent = {
                clientX: touch.clientX,
                clientY: touch.clientY,
                pageX: touch.pageX,
                pageY: touch.pageY,
                pointerId: touch.identifier || 1,
                pointerType: 'touch' as const,
                button: 0,
                buttons: 1,
                target: e.currentTarget,
                currentTarget: e.currentTarget,
                preventDefault: () => e.preventDefault(),
                stopPropagation: () => e.stopPropagation()
              }
              dragControls.start(pointerLikeEvent as unknown as PointerEvent)
            }
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            pointerEvents='none'
            aria-hidden='true'
          >
            <g opacity='0.2'>
              <path
                d='M18 13.8687C18.5523 13.8687 19 14.3165 19 14.8688C19 15.4211 18.5523 15.8687 18 15.8687H2C1.44772 15.8687 1 15.4211 1 14.8688C1 14.3165 1.44772 13.8687 2 13.8687H18ZM18 8.66875C18.5523 8.66875 19 9.11645 19 9.66875C19 10.2211 18.5523 10.6688 18 10.6688H2C1.44772 10.6688 1 10.2211 1 9.66875C1 9.11645 1.44772 8.66875 2 8.66875H18ZM18 3.46875C18.5523 3.46875 19 3.91647 19 4.46875C19 5.02105 18.5523 5.46875 18 5.46875H2C1.44772 5.46875 1 5.02105 1 4.46875C1 3.91647 1.44772 3.46875 2 3.46875H18Z'
                fill='black'
              />
            </g>
          </svg>
        </div>
      </div>
    </Reorder.Item>
  )
}

interface FilterAndColumnReorderBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  columnMetadata: ColumnItem[]
  initialColumnOrder: string[]
  onColumnReorder: (newOrder: string[]) => void
  peerAnalyserDisplayFilters?: string[]
}

// Mapping from enum strings to column IDs
const ENUM_TO_COLUMN_ID_MAP: Record<string, string> = {
  RANK_AND_PERFORMANCE: 'powerRankColumn',
  ELITE_SCORE: 'eliteScores',
  ROLLING_RETURNS: 'rollingReturns',
  TRAILING_RETURNS: 'trailingReturns',
  AUM_CHANGE: 'aumChange',
  AUM_AND_FUND_AGE: 'fundAgeAndAum'
}

// Reverse mapping from column IDs to enum strings
const COLUMN_ID_TO_ENUM_MAP: Record<string, string> = {
  powerRankColumn: 'RANK_AND_PERFORMANCE',
  eliteScores: 'ELITE_SCORE',
  rollingReturns: 'ROLLING_RETURNS',
  trailingReturns: 'TRAILING_RETURNS',
  aumChange: 'AUM_CHANGE',
  fundAgeAndAum: 'AUM_AND_FUND_AGE'
}

// Default order of all 6 columns
const DEFAULT_DISPLAY_FILTERS = [
  'RANK_AND_PERFORMANCE',
  'ELITE_SCORE',
  'ROLLING_RETURNS',
  'TRAILING_RETURNS',
  'AUM_CHANGE',
  'AUM_AND_FUND_AGE'
]

export default function FilterAndColumnReorderBottomSheet({
  isOpen,
  onClose,
  columnMetadata,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialColumnOrder, // Kept for backward compatibility, but order is now derived from peerAnalyserDisplayFilters
  onColumnReorder,
  peerAnalyserDisplayFilters
}: FilterAndColumnReorderBottomSheetProps) {
  const setSelectedCategoryId = usePeerAnalyserStore((state) => state.setSelectedCategoryId)
  const fundNature = usePeerAnalyserStore((state) => state.fundNature)
  const setFundNature = usePeerAnalyserStore((state) => state.setFundNature)
  const { showToast } = useToast()
  const { mutate: reorderColumnsMutation } = useReorderColumns()

  const [tempSelectedTabId, setTempSelectedTabId] = useState<BrowseTabId | string>('')
  const prevIsOpenRef = useRef(false)
  // Stable id (not useId) so WebView aria-labelledby resolves — Taxation Rules / FY pattern (B15)
  const categoryHeadingId = SELECT_CATEGORY_HEADING_ID
  const displaySettingsHeadingId = useId()
  const categoryRadioName = useId()

  // Build the 6 required columns: always show all 6, ordered by peerAnalyserDisplayFilters
  const requiredColumnsOrder = useMemo(() => {
    // Get all 6 column IDs from the default filters
    const allColumnIds = DEFAULT_DISPLAY_FILTERS.map((enumValue) => ENUM_TO_COLUMN_ID_MAP[enumValue]).filter(
      (colId): colId is string => !!colId && columnMetadata.some((col) => col.id === colId)
    )

    if (!peerAnalyserDisplayFilters || peerAnalyserDisplayFilters.length === 0) {
      // If no filters provided, use default order
      return allColumnIds
    }

    // Order columns based on peerAnalyserDisplayFilters
    const orderedColumns: string[] = []
    const remainingColumns = new Set(allColumnIds)

    // First, add columns in the order they appear in peerAnalyserDisplayFilters
    peerAnalyserDisplayFilters.forEach((enumValue) => {
      const colId = ENUM_TO_COLUMN_ID_MAP[enumValue]
      if (colId && remainingColumns.has(colId)) {
        orderedColumns.push(colId)
        remainingColumns.delete(colId)
      }
    })

    // Then append any remaining columns that weren't in peerAnalyserDisplayFilters
    orderedColumns.push(...Array.from(remainingColumns))

    return orderedColumns
  }, [peerAnalyserDisplayFilters, columnMetadata])

  // Get selected columns based on peerAnalyserDisplayFilters
  // Checkboxes are true if the column enum is in the array, false otherwise
  const selectedColumnsFromFilters = useMemo(() => {
    if (!peerAnalyserDisplayFilters || peerAnalyserDisplayFilters.length === 0) {
      // If no filters provided, select all 6 columns
      return new Set(requiredColumnsOrder)
    }
    // Map enum strings to column IDs and create a set - only columns in the array are selected
    return new Set(
      peerAnalyserDisplayFilters
        .map((enumValue) => ENUM_TO_COLUMN_ID_MAP[enumValue])
        .filter((colId): colId is string => !!colId && columnMetadata.some((col) => col.id === colId))
    )
  }, [peerAnalyserDisplayFilters, columnMetadata, requiredColumnsOrder])

  // Initialize selected columns set
  const [tempSelectedColumns, setTempSelectedColumns] = useState<Set<string>>(selectedColumnsFromFilters)

  // Initialize column order: always use the order from peerAnalyserDisplayFilters
  const [tempColumnOrder, setTempColumnOrder] = useState<string[]>(requiredColumnsOrder)

  const { data: backendData } = useQuery({
    queryKey: ['browseCategories'],
    queryFn: fetchBrowseCategories,
    retry: 1,
    refetchOnWindowFocus: false
  })

  // Build data from API response
  const browseCategoriesDataTransformed: BrowseTabData[] = useMemo(() => {
    return backendData ? buildBrowseCategoriesData(backendData) : []
  }, [backendData])

  // Set initial selected tab when data is loaded
  useEffect(() => {
    if (browseCategoriesDataTransformed?.length > 0 && !tempSelectedTabId && isOpen) {
      const currentFundNature = fundNature || FundNature.EQUITY
      const tabIdFromFundNature = getTabIdFromFundNature(currentFundNature)
      const matchingTab = browseCategoriesDataTransformed?.find((tab) => tab?.id === tabIdFromFundNature)
      const tabToSelect = matchingTab || browseCategoriesDataTransformed?.[0]

      if (tabToSelect?.id) {
        setTempSelectedTabId(tabToSelect.id)
      }
    }
  }, [browseCategoriesDataTransformed, tempSelectedTabId, fundNature, isOpen])

  // Reset temp selection when modal opens (only on open, not when dependencies change)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Only reset when modal first opens, not on subsequent dependency changes
      const currentFundNature = fundNature || FundNature.EQUITY
      const tabIdFromFundNature = getTabIdFromFundNature(currentFundNature)
      setTempSelectedTabId(tabIdFromFundNature)

      // Use selected columns from filters, ensuring powerRankColumn is always included (locked item)
      const selectedColumnsWithLocked = new Set(selectedColumnsFromFilters)
      selectedColumnsWithLocked.add('powerRankColumn')
      setTempSelectedColumns(selectedColumnsWithLocked)

      // Use the required columns order
      setTempColumnOrder(requiredColumnsOrder)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, fundNature, selectedColumnsFromFilters, requiredColumnsOrder])

  const handleTabSelect = (tabId: string) => {
    if (!tabId) return
    lightImpactVibrate()
    setTempSelectedTabId(tabId)
  }

  const handleColumnToggle = (columnId: string) => {
    // Prevent toggling locked items (Rank And Performance)
    if (columnId === 'powerRankColumn') {
      return
    }
    lightImpactVibrate()
    setTempSelectedColumns((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(columnId)) {
        newSet.delete(columnId)
      } else {
        newSet.add(columnId)
      }
      return newSet
    })
  }

  const handleColumnReorder = (newOrder: string[]) => {
    setTempColumnOrder(newOrder)
  }

  const handleDone = () => {
    if (!tempSelectedTabId) return

    // Validate at least 3 metrics are selected
    if (tempSelectedColumns.size < 3) {
      showToast({
        message: 'You need to select at least 3 metrics',
        width: '300px',
        zIndex: 100000,
        duration: 1500,
        position: 'bottom-20'
      })
      return
    }

    lightImpactVibrate()

    // If user selected a different category tab, update global fund nature / category once
    const currentFundNature = fundNature || FundNature.EQUITY
    const currentTabIdForFundNature = getTabIdFromFundNature(currentFundNature)
    if (tempSelectedTabId !== currentTabIdForFundNature) {
      const selectedTab = browseCategoriesDataTransformed?.find((tab) => tab?.id === tempSelectedTabId)
      const firstCategory = selectedTab?.sections?.[0]?.categories?.[0]

      if (firstCategory?.id) {
        setSelectedCategoryId(firstCategory.id)
      }
      setFundNature(getFundNatureFromTabId(tempSelectedTabId))
    }

    // Apply column selection - maintain order from tempColumnOrder, filter to only selected ones
    const selectedColumnsArray = tempColumnOrder.filter((id: string) => tempSelectedColumns.has(id))
    onColumnReorder(selectedColumnsArray)

    // Map column IDs to enum strings and call API
    const filtersArray: PeerAnalyserDisplayFilter[] = selectedColumnsArray
      .map((columnId) => COLUMN_ID_TO_ENUM_MAP[columnId])
      .filter((enumValue): enumValue is PeerAnalyserDisplayFilter => !!enumValue)

    // Call API to save column order and selection
    if (filtersArray.length > 0) {
      reorderColumnsMutation(filtersArray)
    }

    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'PeerAnalyser',
        buttonLabel: 'Done',
        eventId: tempSelectedTabId
      }
    })

    onClose()
  }

  return (
    <VaulDrawer
      dismissible={false}
      open={isOpen}
      setOpen={onClose}
      onDismiss={onClose}
      titleId={categoryHeadingId}
      // Power Select_B11: focus lands on the Select Category heading once the sheet is on screen
      focusDrawerOnOpen
    >
      <div className='flex h-auto max-h-[calc(100vh-100px)] min-h-[250px] flex-col overflow-hidden'>
        {/* Header */}
        <div className='px-5 py-4 sticky top-0 z-20 bg-white flex items-start justify-between gap-3'>
          <h2
            id={categoryHeadingId}
            tabIndex={-1}
            className='text-lg font-matter font-medium text-[#232323] outline-none flex-1 m-0'
          >
            Select Category
          </h2>
          <button
            type='button'
            onClick={() => {
              lightImpactVibrate()
              onClose()
            }}
            aria-label='Close dialog'
            aria-description='double tap to close dialog'
            className='flex-shrink-0 min-h-6 min-w-6 flex items-center justify-center border-none bg-transparent p-1 cursor-pointer'
          >
            <span aria-hidden='true'>
              <RenderHTML innerHTMLText={getCloseIcon('#232323')} />
            </span>
          </button>
        </div>

        {/* Scrollable Content Area — tabIndex=-1 avoids whole-region TalkBack stop (B24) */}
        <div
          className='flex-1 overflow-y-auto'
          tabIndex={-1}
          style={{
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* Category options as radios (Power Select_B13–B16) + list grouping (iOS B21 / Android B17) */}
          {browseCategoriesDataTransformed && browseCategoriesDataTransformed.length > 0 && (
            <div role='radiogroup' aria-labelledby={categoryHeadingId}>
              {/* role=list restores SR list context when list-style is none */}
              <ul role='list' className='list-none m-0 p-0'>
                {browseCategoriesDataTransformed.map((tab) => {
                  if (!tab?.id) return null
                  const isSelected = tempSelectedTabId === tab.id
                  const radioId = `select-category-${tab.id}`

                  return (
                    <li key={tab.id} role='listitem' className='list-none m-0 p-0'>
                      <label
                        htmlFor={radioId}
                        className='flex items-center gap-3 p-5 cursor-pointer transition-colors border-b-[1px] border-grey80'
                      >
                        <div className='flex-1 min-w-0'>
                          {tab.label && (
                            <span aria-hidden='true' className='text-[#232323] text-sm font-medium'>
                              {tab.label}
                            </span>
                          )}
                        </div>
                        <span className='relative flex-shrink-0 w-5 h-5'>
                          <input
                            id={radioId}
                            type='radio'
                            name={categoryRadioName}
                            className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
                            checked={isSelected}
                            onChange={() => handleTabSelect(tab.id)}
                            aria-label={tab.label || tab.id}
                            // Power select_B20: radio hint (Excel: “double tap to select”)
                            aria-description='double tap to select'
                          />
                          <span aria-hidden='true' className='pointer-events-none block'>
                            <RenderHTML innerHTMLText={isSelected ? CheckedIcon : UnCheckedIcon} />
                          </span>
                        </span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Column Selection Section — B18: h3 heading */}
          <div className='px-6 pt-10 pb-4'>
            <h3 id={displaySettingsHeadingId} className='text-xs text-[#6a6e6e] mt-1 font-normal m-0'>
              Display Settings and Order
            </h3>
          </div>

          <div className='rounded-xl relative px-5 vertical-drag mb-4'>
            {/*
              Reorder.Group defaults to ul / Item to li.
              Explicit role=list + aria-labelledby: list-style:none otherwise drops
              list announcement (iOS Power select_B21 / Android Power Select_B17 tickboxes).
            */}
            <Reorder.Group
              axis='y'
              values={tempColumnOrder}
              onReorder={handleColumnReorder}
              role='list'
              aria-labelledby={displaySettingsHeadingId}
              className='bg-[#F6F9F9] list-none m-0 p-0'
            >
              {tempColumnOrder.map((columnId: string, index: number) => {
                const column = columnMetadata.find((col) => col.id === columnId)
                if (!column) return null
                const isSelected = tempSelectedColumns.has(columnId)
                const isLast = index === tempColumnOrder.length - 1
                const isLocked = columnId === 'powerRankColumn'

                return (
                  <ReorderableColumnItem
                    key={columnId}
                    columnId={columnId}
                    column={column}
                    isSelected={isSelected}
                    isLast={isLast}
                    onToggle={handleColumnToggle}
                    isLocked={isLocked}
                  />
                )
              })}
            </Reorder.Group>
          </div>
        </div>

        {/* Done Button */}
        <div
          style={{ paddingBottom: `${getBottomPadding() ? getBottomPadding() + 4 : 16}px` }}
          className='px-6 pt-4 sticky bottom-0 bg-white border-t border-grey80'
        >
          <button
            type='button'
            onClick={handleDone}
            className='w-full py-3 px-4 bg-[#232323] text-white font-matter font-medium rounded-xl'
          >
            Done
          </button>
        </div>
      </div>
    </VaulDrawer>
  )
}
